import { createContext, useContext, useState, ReactNode, useEffect, startTransition, useCallback } from 'react'
import { useLocalStorage } from 'react-use'
import getConfig from 'next/config'

import { EUIFlags, ELocalStorageKeys } from '@/constants'
import { TDevUIFlags } from '@/config'
import { isProd } from '@/constants/server'

/**
 * UI flags context shape defining the currently active flags
 * and the toggle function to update them.
 * @typedef {Object} UIFlagsContextType
 * @property {Record<EUIFlags, boolean>} UIFlags - The current state of UI flags.
 * @property {(key: EUIFlags, value: boolean) => void} handleToggleUIFlag - Function to update a flag value.
 */
type UIFlagsContextType = {
  UIFlags: Record<EUIFlags, boolean>
  handleToggleUIFlag: (key: EUIFlags, value: boolean) => void
  resetAllUIFlags: () => void
  isModified: boolean
}

// Internal React context to hold the flag state
const UIFlagsContext = createContext<UIFlagsContextType | undefined>(undefined)

/**
 * Props for UIFlagsProvider.
 * @typedef {Object} UIFlagsProviderProps
 * @property {ReactNode} children - Child components to be wrapped with the provider.
 * @property {Partial<Record<EUIFlags, boolean>>} [defaultFlags] - Optional flag overrides. Only applied in non-production environments.
 */
type UIFlagsProviderProps = {
  children: ReactNode
  defaultFlags?: Partial<Record<EUIFlags, boolean>>
}

const { publicRuntimeConfig } = getConfig()

/**
 * Top-level context provider for UI flags.
 * In production, this returns a no-op provider to avoid localStorage usage.
 * In development, it delegates to UIFlagsDevProvider which manages state.
 *
 * @param {UIFlagsProviderProps} props
 * @returns {JSX.Element}
 */
export const UIFlagsProvider = ({ children, defaultFlags }: UIFlagsProviderProps) => {
  if (isProd) {
    return (
      <UIFlagsContext.Provider
        value={{
          UIFlags: publicRuntimeConfig?.UI_FLAGS,
          handleToggleUIFlag: () => {},
          resetAllUIFlags: () => {},
          isModified: false,
        }}
      >
        {children}
      </UIFlagsContext.Provider>
    )
  }

  return <UIFlagsDevProvider defaultFlags={defaultFlags}>{children}</UIFlagsDevProvider>
}

const defaultUIFlags: TDevUIFlags = {
  ...publicRuntimeConfig?.UI_FLAGS,
}

/**
 * Provider used only in development environments.
 * It initializes UI flags using localStorage, allows toggling,
 * and persists updates automatically.
 *
 * @param {UIFlagsProviderProps} props
 * @returns {JSX.Element}
 */
const UIFlagsDevProvider = ({ children, defaultFlags }: UIFlagsProviderProps) => {
  // Persist UI flags in localStorage
  const [storedUIFlags, setStoredUIFlags] = useLocalStorage(ELocalStorageKeys.UI_DEV_FLAGS, defaultUIFlags)

  // UI flags state, initialized with merged values
  const [UIFlags, setUIFlags] = useState<Record<EUIFlags, boolean>>({
    ...defaultUIFlags,
    ...storedUIFlags,
    ...defaultFlags,
  })

  /**
   * Updates a single flag in the UIFlags state.
   *
   * @param {EUIFlags} key - The flag key to update.
   * @param {boolean} value - The new value for the flag.
   */
  const handleToggleUIFlag = useCallback((key: EUIFlags, value: boolean) => {
    setUIFlags(prev => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  const resetAllUIFlags = useCallback(() => {
    setUIFlags(defaultUIFlags)
  }, [])

  const isModified = !!storedUIFlags && Object.keys(storedUIFlags).length > 0
  const hasDefaultFlagOverrides = !!defaultFlags && Object.keys(defaultFlags).length > 0

  // Persist updated flags to localStorage, but only when no defaultFlags overrides are provided
  useEffect(() => {
    if (hasDefaultFlagOverrides) return

    const overriddenFlags = Object.entries(UIFlags).reduce(
      (acc, [flag, value]) => {
        if (defaultUIFlags[flag as EUIFlags] != null && value !== defaultUIFlags[flag as EUIFlags]) {
          acc[flag as EUIFlags] = value
        }

        return acc
      },
      {} as Record<EUIFlags, boolean>,
    )

    startTransition(() => {
      setStoredUIFlags(overriddenFlags)
    })
  }, [UIFlags, setStoredUIFlags, defaultFlags, hasDefaultFlagOverrides])

  return (
    <UIFlagsContext.Provider value={{ UIFlags, handleToggleUIFlag, resetAllUIFlags, isModified }}>
      {children}
    </UIFlagsContext.Provider>
  )
}

/**
 * Custom hook to access UI flags context.
 * Throws if used outside of a UIFlagsProvider.
 *
 * @throws {Error} if used outside of the provider
 * @returns {UIFlagsContextType}
 */
export const useUIFlags = () => {
  const context = useContext(UIFlagsContext)

  if (context === undefined) {
    throw new Error('useUIFlags must be used within a UIFlagsProvider')
  }

  return context
}
